import {
  getSetupChecklistItemLabelKey,
  SetupChecklistItemId,
} from '@griply/utils';
import { endOfDay } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SetupChecklistDisplayItem } from 'shared/components/ui/setup-checklist-card';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useOpenSettings } from 'shared/contexts/user-settings';
import { SettingsSection } from 'shared/dialogs/user-settings/settings-nav';
import { Paths } from 'shared/routes/paths';

import { useSetupChecklistItems } from './use-setup-checklist-items';

export type SetupChecklistDisplay = {
  displayItems: SetupChecklistDisplayItem[];
  completeCount: number;
  totalCount: number;
  allComplete: boolean;
};

// Translated, navigation-wired items in display order (complete first).
export const useSetupChecklistDisplayItems = (): SetupChecklistDisplay => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = useToday();
  const openCreateTask = useOpenCreateTask();
  const openSettings = useOpenSettings();

  const { items, allComplete } = useSetupChecklistItems();

  const onGoals = useCallback(() => navigate(Paths.Goals), [navigate]);
  const onAddTask = useCallback(
    () => openCreateTask({ endStrategy: { deadline: endOfDay(today) } }),
    [openCreateTask, today],
  );
  const onUpcoming = useCallback(() => navigate(Paths.Upcoming), [navigate]);
  const onHabits = useCallback(() => navigate(Paths.Habits), [navigate]);
  const onCalendarSettings = useCallback(
    () => openSettings(SettingsSection.Calendar),
    [openSettings],
  );

  const navigationById = useMemo(
    (): Record<SetupChecklistItemId, (() => void) | undefined> => ({
      [SetupChecklistItemId.CreateAccount]: undefined,
      [SetupChecklistItemId.FirstGoal]: onGoals,
      [SetupChecklistItemId.FirstTask]: onAddTask,
      [SetupChecklistItemId.PlanTomorrow]: onUpcoming,
      [SetupChecklistItemId.Habits]: onHabits,
      [SetupChecklistItemId.ConnectCalendar]: onCalendarSettings,
    }),
    [onGoals, onAddTask, onUpcoming, onHabits, onCalendarSettings],
  );

  const displayItems = useMemo((): SetupChecklistDisplayItem[] => {
    const withDisplay = items.map((item) => ({
      id: item.id,
      label: t(getSetupChecklistItemLabelKey(item)),
      complete: item.complete,
      onNavigate: navigationById[item.id],
    }));

    return [
      ...withDisplay.filter((item) => item.complete),
      ...withDisplay.filter((item) => !item.complete),
    ];
  }, [items, navigationById, t]);

  return {
    displayItems,
    completeCount: items.filter((item) => item.complete).length,
    totalCount: items.length,
    allComplete,
  };
};
